import type { SlotCandidate } from './availability-api';

/**
 * WHICH member of a team-calendar slot gets the appointment — legacy `slotClick` in
 * `View/js/cart-gcal.js` (:797-857), as one pure function. A team calendar shows ONE merged set of
 * times; the member is only decided when a time is clicked, and the hold and the save are then for
 * that one member (`reserveSlot`, :625-716).
 *
 * The steps, in legacy's order:
 *  1. one candidate  -> that member (:856-857);
 *  2. favourites     -> keep only the shopper's favourites; when none of the candidates is one,
 *                       keep them all (:805-808);
 *  3. `teamSelect`   -> 1: nothing more here (the rank in step 4 decides);
 *                       2: fewest open appointments — the minimum among members whose count is
 *                          known; members with no count are ignored (:814-823);
 *                       3: lowest service-area priority among members that have one (:824-839);
 *                       anything else behaves as 1 (legacy's `default:` is dead code);
 *                       a step that keeps nobody falls back to everyone from step 2 (:842);
 *  4. rank           -> keep the members with the lowest non-zero rank. A rank of 0 / missing is
 *                       ignored, so once anyone is ranked the unranked members drop out (:843-851);
 *  5. random         -> one of whoever is left (:852).
 *
 * No React and no I/O: `random` is injectable so a test can pin the tie-break.
 */

/** Design var `cart-gcal.teamselect`. */
export type TeamSelect = 1 | 2 | 3;

export interface ResolveSlotMemberOptions {
    /** The shopper's favourite members. */
    favoriteIds?: readonly number[];
    /** Default 1. */
    teamSelect?: TeamSelect | number;
    /** Open-order count per member id — the availability answer's `open` map. `teamSelect` 2. */
    openOrders?: Readonly<Record<number, number>>;
    /** Service-area priority per member id (lower wins). `teamSelect` 3. */
    priorities?: Readonly<Record<number, number | null | undefined>>;
    /** `Math.random` by default; returns a number in [0, 1). */
    random?: () => number;
}

/** The member a slot resolves to, or `undefined` when the slot names nobody. */
export function resolveSlotMember(
    candidates: readonly SlotCandidate[],
    options: ResolveSlotMemberOptions = {}
): number | undefined {
    if (candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0].id;

    const { favoriteIds = [], teamSelect = 1, openOrders, priorities } = options;
    const random = options.random ?? Math.random;

    // 2. Favourites, when any of the candidates is one.
    const favourites = candidates.filter(candidate => favoriteIds.includes(candidate.id));
    const pool = favourites.length > 0 ? favourites : [...candidates];

    // 3. The brand's team-select rule.
    let preferred: SlotCandidate[] = pool;
    if (teamSelect === 2 || teamSelect === 3) {
        const source = teamSelect === 2 ? openOrders : priorities;
        const valueOf = (id: number): number | undefined => {
            const value = source?.[id];
            return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
        };
        const known = pool
            .map(candidate => valueOf(candidate.id))
            .filter((value): value is number => value !== undefined);
        if (known.length > 0) {
            const lowest = Math.min(...known);
            preferred = pool.filter(candidate => valueOf(candidate.id) === lowest);
        }
    }

    // 4. The lowest non-zero rank; with nobody ranked, everyone stays.
    const ranks = preferred.map(candidate => candidate.rank).filter(rank => rank > 0);
    const finalists =
        ranks.length > 0
            ? preferred.filter(candidate => candidate.rank === Math.min(...ranks))
            : preferred;

    // 5. Random among equals. Clamped, so an injected `random` returning 1 cannot run off the end.
    const index = Math.min(Math.floor(random() * finalists.length), finalists.length - 1);
    return finalists[Math.max(index, 0)].id;
}
